import {
  Button,
  Modal,
  ModalBody,
  Flex,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  FormControl,
  FormLabel,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { finleyAssert } from '@finley-technologies/common';

import { AccountTypeGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { useBankAccountTypeOptions } from './hooks';
import { useUpsertImplementationPortalBankAccountMutation } from './parties.gql.generated';
import { Icon } from 'packages/common-frontend/components/icons';
import { ErrorBoundary } from '~/contexts/MonitoringProvider';
import { FacilityDetailsRouteParamsStruct } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/helper';
import { Organization } from '~/core/routes/settings/implementations/common/types';
import { Select } from '~/packages/common-frontend/components/Select';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { errorToastConfig } from '~/packages/common-frontend/constants';

type BankAccount = {
  id: string;
  name: string;
  type: AccountTypeGQLType;
};

type AddBankAccountModalProps = {
  partyType: 'borrowers' | 'capitalProviders';
  party: Organization;
  onAddBankAccount: (bankAccount: BankAccount) => void;
};
const AddBankAccountButtonBase = ({
  partyType,
  party,
  onAddBankAccount,
}: AddBankAccountModalProps) => {
  const params = useParams();
  const { implementationId } = FacilityDetailsRouteParamsStruct.parse(params);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const accountTypeOptions = useBankAccountTypeOptions(party, partyType);
  const [accountType, setAccountType] = useState<AccountTypeGQLType>();

  useEffect(() => {
    // Reset everything when the modal is opened
    if (isOpen) {
      setAccountType(undefined);
    }
  }, [isOpen]);

  const [
    upsertBankAccount,
    { loading: upsertBankAccountLoading },
  ] = useUpsertImplementationPortalBankAccountMutation();

  const allowButtonClick = !!accountType;
  const onButtonClick = async () => {
    try {
      if (accountType) {
        // Note: the credit agreement does actually support multiple bank accounts of the same
        // type, and this setup can be authored in admin dashboard. Implementation will preserve
        // existing such setups, but does not currently support authoring it.
        const result = await upsertBankAccount({
          variables: {
            implementationId,
            ownerId: party.id,
            accountType,
          },
        });

        const upsertedAccount =
          result.data?.upsertImplementationPortalBankAccount;
        finleyAssert(upsertedAccount, 'Failed to ensure bank account.');

        onAddBankAccount({
          id: upsertedAccount.id,
          name: upsertedAccount.name,
          // Note: this is a credit agreement property, not a
          // property of the bank account entity itself.
          type: accountType,
        });
        onClose();
      }
    } catch (e) {
      Sentry.captureException(e);
      toast({
        ...errorToastConfig,
        title: 'Failed to ensure bank account.',
      });
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Bank Account</ModalHeader>
          <ModalCloseButton />
          <ModalBody display="flex" flexDirection="column" gap="1rem">
            <Typography variant="base-muted">
              Add bank account for {party.name}. Only one account of each type
              is allowed.
            </Typography>
            <FormControl>
              <Flex direction="column" gap="0.5rem">
                <FormLabel fontWeight="bold">Account Type</FormLabel>
                <Select
                  value={accountType}
                  placeholderLabel="Select Account Type"
                  onChange={(value) => setAccountType(value)}
                  options={accountTypeOptions}
                  width="100%"
                />
              </Flex>
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button
              variant="secondary"
              colorScheme="v2-aurora"
              mr={3}
              onClick={onClose}
            >
              Cancel
            </Button>

            <Button
              colorScheme="v2-aurora"
              isLoading={upsertBankAccountLoading}
              isDisabled={!allowButtonClick}
              onClick={onButtonClick}
            >
              Add Bank Account
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Tooltip
        label={
          accountTypeOptions.length === 0
            ? 'All available bank account types have been added.'
            : ''
        }
      >
        <Button
          isDisabled={accountTypeOptions.length === 0}
          variant="v2-ghost"
          color="v2-aurora"
          leftIcon={<Icon icon="plus" />}
          onClick={onOpen}
        >
          Add Bank Account
        </Button>
      </Tooltip>
    </>
  );
};

export const AddBankAccountButton = (props: AddBankAccountModalProps) => (
  <ErrorBoundary
    fallback={({ message, showErrorToast }) => {
      showErrorToast(message);
      return <></>;
    }}
  >
    <AddBankAccountButtonBase {...props} />
  </ErrorBoundary>
);
